import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";
import { IconButton } from "./IconButton";
import { IconClose, IconError, IconInfo, IconSuccess, IconWarning } from "./icons";
import {
  TOAST_DEFAULT_DURATION,
  ToastContext,
  type ToastApi,
  type ToastOptions,
  type ToastTone,
} from "./toastContext";
import "./Toast.css";

const TONE_ICON: Record<ToastTone, ReactNode> = {
  neutral: <IconInfo />,
  info: <IconInfo />,
  success: <IconSuccess />,
  warning: <IconWarning />,
  danger: <IconError />,
};

export interface ToastProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  tone?: ToastTone;
  title: ReactNode;
  message?: ReactNode;
  action?: ReactNode;
  /** ms before onDismiss fires; 0 disables. Paused while hovered or focused. */
  duration?: number;
  onDismiss?: () => void;
}

/** One notification. Usually rendered by ToastProvider; usable standalone (e.g. in Kit). */
export function Toast({
  tone = "neutral",
  title,
  message,
  action,
  duration = TOAST_DEFAULT_DURATION,
  onDismiss,
  className,
  ...rest
}: ToastProps) {
  const [paused, setPaused] = useState(false);
  const remaining = useRef(duration);
  const startedAt = useRef(0);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    if (!duration || paused || !dismissRef.current) return;
    startedAt.current = Date.now();
    const id = window.setTimeout(() => dismissRef.current?.(), remaining.current);
    return () => {
      window.clearTimeout(id);
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
    };
  }, [duration, paused]);

  return (
    <div
      className={cx("ui-toast", `ui-toast--${tone}`, className)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      {...rest}
    >
      <span className="ui-toast__icon" aria-hidden="true">
        {TONE_ICON[tone]}
      </span>
      <div className="ui-toast__body">
        <p className="ui-toast__title">{title}</p>
        {message ? <p className="ui-toast__message">{message}</p> : null}
        {action ? <div className="ui-toast__action">{action}</div> : null}
      </div>
      {onDismiss ? (
        <IconButton
          className="ui-toast__close"
          size="sm"
          label="Dismiss notification"
          icon={<IconClose />}
          onClick={onDismiss}
        />
      ) : null}
    </div>
  );
}

interface QueuedToast extends ToastOptions {
  id: string;
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Max toasts on screen; oldest are dropped first. Default 4. */
  limit?: number;
}

let counter = 0;

/** Holds the toast queue and renders the polite live region. Wrap the app once. */
export function ToastProvider({ children, limit = 4 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<QueuedToast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (options: ToastOptions) => {
      const id = options.id ?? `toast-${++counter}`;
      setToasts((list) => {
        const without = list.filter((t) => t.id !== id);
        return [...without, { ...options, id }].slice(-limit);
      });
      return id;
    },
    [limit],
  );

  const clear = useCallback(() => setToasts([]), []);
  const api = useMemo<ToastApi>(() => ({ show, dismiss, clear }), [show, dismiss, clear]);

  const region = (
    <div className="ui-toast-region" aria-live="polite" aria-label="Notifications" role="region">
      {toasts.map((t) => (
        <Toast
          key={t.id}
          tone={t.tone}
          title={t.title}
          message={t.message}
          action={t.action}
          duration={t.duration ?? TOAST_DEFAULT_DURATION}
          onDismiss={() => dismiss(t.id)}
        />
      ))}
    </div>
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== "undefined" ? createPortal(region, document.body) : null}
    </ToastContext.Provider>
  );
}
